import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { PLATFORM_DP_ENVIRONMENT } from '@jfw/angular/shared/tokens';
import {
  EztBasicStyleInfo,
  EztProductSearchResult,
  EztSimilarStyle,
  EztStyleSize,
  InventoryCheckMatrix,
  InventoryCheckV2,
  InventoryCheckV2Response,
  InventoryMatrixResponse,
  ProductListRequest,
  SimilarStylesRequest,
  StyleProdId,
  StyleProdIdType,
  ValidateItemRequest,
  ValidateItemResponse,
} from '@jfw/types';
import {
  catchError,
  map,
  Observable,
  of,
  retry,
  shareReplay,
  timeout,
} from 'rxjs';

// A definitive negative (false/null) already means "the backend answered no"
// throughout this file's own consumers (WholesaleItemControllerService's
// retryUnresolvedStages() etc.) -- this sentinel lets a caller tell that
// apart from "the request itself never got a real answer" (retries
// exhausted), so it can leave the row's validation state unresolved
// (retryable) instead of writing a false rejection with no way to recover.
// See EXT-6061.
export const INVENTORY_CHECK_ERRORED = Symbol('inventory-check-errored');
export type InventoryCheckErrored = typeof INVENTORY_CHECK_ERRORED;

// EXT-5979: checkStyleAndSizeValidity used to collapse its response down to
// a bare boolean, discarding the backend's validation-resolved opt1/opt2 --
// this surfaces it so a caller can use a real resolved size instead of the
// raw wire value.
export interface StyleAndSizeValidityResult {
  isAvailable: boolean;
  resolvedOption1: string;
  resolvedOption2: string;
  // EXT-6268: the resolved ITEM number (null when unresolved).
  itemNumber: number | null;
}

@Injectable({
  providedIn: 'root',
})
export class WholesaleInventoryService {
  private readonly environment = inject(PLATFORM_DP_ENVIRONMENT);
  private readonly inventoryApiUrl = this.environment.inventory_api_server_url;

  private readonly http = inject(HttpClient);

  private readonly reqHeader = new HttpHeaders({
    'Content-Type': 'application/json',
  });

  private readonly styleDescriptionCache = new Map<
    string,
    Observable<EztBasicStyleInfo | null | InventoryCheckErrored>
  >();

  // EXT-6532: one inventory-check attempt's ceiling -- above the server's own
  // 10 s DB2 timeout plus a little queueing, so a check the server can still
  // answer isn't cut short.
  public static readonly CHECK_ATTEMPT_TIMEOUT_MS = 15_000;

  private static readonly fixedProductListTypes: StyleProdIdType[] = [
    StyleProdId.Coat,
    StyleProdId.Pant,
    StyleProdId.Shirt,
    StyleProdId.Tie,
    StyleProdId.Vest,
    StyleProdId.Shoe,
    StyleProdId.PocketSquare,
    StyleProdId.Jewelry,
  ];

  public getAvailableSizesByStyle(
    prodId: StyleProdIdType,
    style: string,
  ): Observable<EztStyleSize[]> {
    const productListApiUrl = `${this.inventoryApiUrl}/getAvailableSizes/${prodId}/${style}`;
    return this.http.get<EztStyleSize[]>(productListApiUrl, {
      headers: this.reqHeader,
    });
  }

  public getSimilarStyles(
    whse: string,
    prodId: StyleProdIdType,
    style: string,
  ): Observable<EztSimilarStyle[]> {
    const similarStylesApiUrl = `${this.inventoryApiUrl}/similar-styles`;
    const request: SimilarStylesRequest = {
      whse,
      prodId: prodId as SimilarStylesRequest['prodId'],
      style,
    };

    return this.http.post<EztSimilarStyle[]>(similarStylesApiUrl, request, {
      headers: this.reqHeader,
    });
  }

  public getProductList(
    prodId: StyleProdIdType,
    whse: string,
  ): Observable<EztProductSearchResult[]> {
    if (WholesaleInventoryService.fixedProductListTypes.includes(prodId)) {
      const productListApiUrl = `${this.inventoryApiUrl}/product-list`;
      const request: ProductListRequest = {
        productId: prodId as ProductListRequest['productId'],
        warehouse: whse as ProductListRequest['warehouse'],
      };

      return this.http.post<EztProductSearchResult[]>(
        productListApiUrl,
        request,
        { headers: this.reqHeader },
      );
    }

    const legacyProductListApiUrl = `${this.inventoryApiUrl}/getProductList/${prodId}/${whse}`;

    return this.http.get<EztProductSearchResult[]>(legacyProductListApiUrl, {
      headers: this.reqHeader,
    });
  }

  /**
   * The style's state on `asOf` (YYYY-MM-DD), the order's use date, or today
   * without one (EXT-6333). Submit checks the use date, so order entry should
   * pass it: an upcoming style is valid for an event after it activates.
   */
  public getStyleDescription(
    prodId: StyleProdIdType,
    styleCode: string,
    asOf?: string | null,
  ): Observable<EztBasicStyleInfo | null | InventoryCheckErrored> {
    const cacheKey = `${prodId}_${styleCode.toUpperCase()}_${asOf ?? ''}`;
    if (this.styleDescriptionCache.has(cacheKey)) {
      return this.styleDescriptionCache.get(cacheKey)!;
    }

    const getStyleDescriptionApiUrl =
      `${this.inventoryApiUrl}/getStyleDescription/${prodId}/${styleCode}` +
      (asOf ? `?asOf=${asOf}` : '');

    const request$ = this.http
      .get<EztBasicStyleInfo>(getStyleDescriptionApiUrl, {
        headers: this.reqHeader,
      })
      .pipe(
        // EXT-5989: a single transient failure here used to permanently latch
        // WholesaleItemControllerService.onStyleMatchedFromInput() at
        // isValidStyle: false ("Invalid Style" shown to the user) -- once set,
        // retryUnresolvedStages()'s own recovery guard (isValidStyle !== true)
        // never re-attempts it, since a caught error and a genuine style-not-
        // found both collapse to the same null response. Retry before falling
        // back to null, same idiom as event.service.ts's
        // getWarehouseByAccountAndDate (EXT-5980).
        retry({ count: 2, delay: 1000 }),
        catchError((error) => {
          console.error('Error fetching style description:', error);
          this.styleDescriptionCache.delete(cacheKey); // clear failed request from cache
          // EXT-6061: emit the errored sentinel, not null -- null already
          // means "checked, genuinely not found/unavailable" elsewhere in
          // this response's own consumers. Collapsing both here is exactly
          // what left retryUnresolvedStages() unable to tell a retryable
          // network failure apart from a real negative answer.
          return of(INVENTORY_CHECK_ERRORED);
        }),
        shareReplay(1),
      );

    this.styleDescriptionCache.set(cacheKey, request$);
    return request$;
  }

  public checkStyleAndSizeValidity(
    prodId: StyleProdIdType,
    styleCode: string,
    size1?: string | null,
    size2?: string | null,
    size3?: string | null,
    whse?: string | null,
  ): Observable<StyleAndSizeValidityResult | InventoryCheckErrored> {
    const validateItemApiUrl = `${this.inventoryApiUrl}/validate-item`;

    // EXT-5979: the backend now resolves opt1/opt2 via normalizeSizeInputs
    // (the same normalization validateOrder uses), which -- unlike the old
    // MEASSCAL-join lookup this replaced -- expects PT's waist+rise as a
    // single combined value (e.g. "34R") and splits it internally. size1
    // already arrives pre-combined for PT (see item-controller.service.ts's
    // validationInput), so no prodId-specific handling is needed here.
    const oropt1 = size1 || '';
    const oropt2 = size2 || '';

    const request: ValidateItemRequest = {
      whse: whse || '',
      orprod: prodId,
      orstyl: styleCode,
      oropt1,
      oropt2,
    };

    return this.http
      .post<ValidateItemResponse>(validateItemApiUrl, request, {
        headers: this.reqHeader,
      })
      .pipe(
        map((response): StyleAndSizeValidityResult => {
          if (Array.isArray(response) && response.length > 0) {
            // EXT-5979: surface the validation-resolved size instead of
            // discarding it -- a caller (e.g. order-entry's live
            // unrealistic-sizes check) can use this instead of the raw wire
            // value. If itemNumber -> Valid, If Null -> Invalid.
            const match =
              response.find((item) => item.item !== null) ?? response[0];
            return {
              isAvailable: response.some((item) => item.item !== null),
              resolvedOption1: match.opt1,
              resolvedOption2: match.opt2,
              itemNumber: match.item ?? null,
            };
          }
          return {
            isAvailable: false,
            resolvedOption1: oropt1,
            resolvedOption2: oropt2,
            itemNumber: null,
          };
        }),
        // EXT-5989: same shape as getStyleDescription above -- a single
        // transient failure used to permanently latch this row's
        // isStyleAndSizeAvailable at false ("Size Invalid" shown to the
        // user), and retryUnresolvedStages() only re-attempts a stage still
        // at null, never one an error already forced to false. Retry first.
        retry({ count: 2, delay: 1000 }),
        catchError((error) => {
          console.error('Error in checkStyleAndSizeValidity:', error);
          // EXT-6061: see getStyleDescription's identical comment above --
          // false already means "genuinely not available" to this method's
          // consumers, so an exhausted-retry failure needs its own sentinel
          // rather than reusing it.
          return of(INVENTORY_CHECK_ERRORED);
        }),
      );
  }

  public checkInventoryAvailability(
    request: InventoryCheckV2,
  ): Observable<
    { isAvailable: boolean; errorMessage: string } | InventoryCheckErrored
  > {
    const checkInventoryApiUrl = `${this.inventoryApiUrl}/check`;

    return this.http
      .post<InventoryCheckV2Response>(checkInventoryApiUrl, request, {
        headers: this.reqHeader,
      })
      .pipe(
        map((response) => ({
          isAvailable: response.bookable,
          // EXT-6336: an item with no sizes (a pocket square) isn't
          // unavailable in a size.
          errorMessage: response.bookable
            ? ''
            : request.option1 || request.option2
              ? 'Size Unavailable'
              : 'Unavailable',
        })),
        // EXT-5989: same shape as checkStyleAndSizeValidity above -- a single
        // transient failure used to permanently latch this row's isValid at
        // false ("Inventory Check Failed" shown to the user), and
        // retryUnresolvedStages() only re-attempts a stage still at null,
        // never one an error already forced to false. Retry first.
        // EXT-6532: each attempt is bounded, since a check queued behind a
        // cold AS400 job can hang for 30 s+ before the server gives up, and
        // three of those left the row spinning for well over a minute.
        // timeout() sits before retry() so it times out each attempt, not
        // the whole sequence.
        timeout(WholesaleInventoryService.CHECK_ATTEMPT_TIMEOUT_MS),
        retry({ count: 2, delay: 1000 }),
        catchError((error) => {
          console.error('Error in checkInventoryAvailability:', error);
          // EXT-6061: see getStyleDescription's identical comment above --
          // {isAvailable: false, ...} already means "genuinely unavailable"
          // to this method's consumers, so an exhausted-retry failure needs
          // its own sentinel rather than reusing that shape.
          return of(INVENTORY_CHECK_ERRORED);
        }),
      );
  }

  public getAlternateMatrix(
    request: InventoryCheckMatrix,
  ): Observable<InventoryMatrixResponse> {
    const alternateMatrixApiUrl = `${this.inventoryApiUrl}/alternate-matrix`;

    return this.http.post<InventoryMatrixResponse>(
      alternateMatrixApiUrl,
      request,
      { headers: this.reqHeader },
    );
  }

  public checkStyleValidity(
    prodId: StyleProdIdType,
    style: string,
    asOf?: string | null,
  ): Observable<EztBasicStyleInfo | null | InventoryCheckErrored> {
    return this.getStyleDescription(prodId, style, asOf).pipe(
      map((basicStyleInfo) => {
        // EXT-6061: pass the errored sentinel straight through -- it must
        // not fall into the `return null` branch below, since null here
        // already means "checked, genuinely invalid/unavailable style."
        if (basicStyleInfo === INVENTORY_CHECK_ERRORED) {
          return basicStyleInfo;
        }
        if (basicStyleInfo && basicStyleInfo.available) {
          return basicStyleInfo;
        }
        return null;
      }),
    );
  }
}
